<template>
	<div
		class="order-feedback"
	>
		<h1
			class="order-feedback__headline insicht insicht--fade-up"
			v-html="$t('order.feedback.title')"
		/>
		<div
			class="order-feedback__text insicht insicht--fade-up"
			v-html="$t('order.feedback.text')"
		/>
		<ul
			v-if="tokenValue"
			class="order-feedback__info insicht insicht--fade-up"
		>
			<li
				v-if="channelName"
				class="order-feedback__info-item"
			>
				<SVGLocation/>
				<span v-html="channelName"/>
			</li>
			<li
				v-if="deliveryDate"
				class="order-feedback__info-item"
			>
				<SVGDate/>
				<span v-html="deliveryDate"/>
			</li>
			<li
				v-if="!timeDisabled && deliveryTime"
				class="order-feedback__info-item"
			>
				<SVGClock/>
				<span v-html="deliveryTime"/>
			</li>
			<li
				v-if="shippingMethod"
				class="order-feedback__info-item"
			>
				<component
					v-if="shippingMethod && shippingMethod.code"
					:is="shippingMethodIconComponent(shippingMethod.code)"
				/>
				<span v-html="shippingMethod.name"/>
			</li>
			<li
				v-if="!paymentDisabled"
				v-for="(payment, i) in payments"
				:key="`payment-${i}`"
				class="order-feedback__info-item"
			>
				<component
					:is="iconComponent(payment.method.code) || iconComponent('datatrans')"
				/>
				<span v-html="payment.method.name"/>
			</li>
			<li
				v-if="!billingAddressDisabled && billingAddress"
				class="order-feedback__info-item"
			>
				<!-- <SVGStore/> -->
				<address v-sanitize="formatAddress(billingAddress)"/>
			</li>
		</ul>

		<div
			v-if="tokenValue"
			class="order-feedback__block insicht insicht--fade-up"
		>
			<h2 class="order-feedback__block-title text--highlight">
				<SVGTip class="order-feedback__block-title-icon"/>
				<span v-html="$t('order.feedback.tipTitle')"/>
			</h2>
			<div class="order-feedback__block-text" v-html="$t('order.feedback.tipText')"/>
			<GeneralMessage
				v-if="tipResponse.message"
				:message="tipResponse.message"
				:type="tipResponse.type"
				@close="tipResponse.message = ''"
			/>

			<TipInput
				:options="cartTipVariant.options"
				:selected="cartTipOption"
				:tip="cartTip"
				:tip-percent="cartTipPercent"
				:locked="loading || tipSubmitted"
				@tip:option="onTipOption"
				@tip:percent="onTipPercent"
				@tip:value="onTipValue"
			/>
			<Button
				v-if="cartTip"
				level="primary"
				:text="$t('order.feedback.tipButton')"
				@click="onTipSubmit"
				:disabled="loading || tipSubmitted"
				small
			/>
		</div>

		<div
			v-if="tokenValue"
			class="order-feedback__block insicht insicht--fade-up"
		>
			<h2 class="order-feedback__block-title">
				<SVGStar class="order-feedback__block-title-icon"/>
				<span v-html="$t('order.feedback.productFeedbackTitle')"/>
			</h2>
			<div class="order-feedback__block-text" v-html="$t('order.feedback.productFeedbackText')"/>

			<ul class="order-feedback__products">
				<li
					v-for="item in itemsWithoutAdditional"
					:key="item.id"
					class="order-feedback__product"
				>
					<div class="order-feedback__product-wrapper">
						<img
							class="order-feedback__product-image"
							:src="item.image"
						/>
						<div class="order-feedback__product-content">
							<ProductRating
								class="order-feedback__product-rating"
								:rating="item.averageRatings"
							/>
							<span
								class="order-feedback__product-name"
								v-html="item.name"
							/>
							<span
								class="order-feedback__product-description"
								v-html="item.shortDescription || item.description"
							/>
						</div>
					</div>
					<Button
						class="order-feedback__product-button"
						:level="productsReviewed[item.code] ? 'secondary' : 'primary'"
						type="button"
						:text="$t('order.feedback.productFeedbackButton')"
						small
						@click.prevent="onProductFeedback(item)"
					/>
				</li>
			</ul>
		</div>

		<template v-if="orderSurveyUrl">
			<div class="order-feedback__block insicht insicht--fade-up">
				<h3
					class="order-feedback__block-title"
					v-html="$t('checkout.success.survey.title')"
				/>
				<div
					class="order-feedback__block-text"
					v-html="$t('checkout.success.survey.text')"
				/>
				<Button
					level="primary"
					:text="$t('checkout.success.survey.button')"
					:href="orderSurveyUrl"
					target="_blank"
					arrow
					small
				/>
			</div>
		</template>

		<template v-if="hasFeature('FEATURE_ORDER_NPS') && orderNpsSurveyUrl">
			<div class="order-feedback__block insicht insicht--fade-up">
				<h3
					class="order-feedback__block-title"
					v-html="$t('checkout.success.nps.title')"
				/>
				<div
					class="order-feedback__block-text"
					v-html="$t('checkout.success.nps.text')"
				/>
				<Button
					level="primary"
					:text="$t('checkout.success.nps.button')"
					:href="orderNpsSurveyUrl"
					target="_blank"
					arrow
					small
				/>
			</div>
		</template>

		<div
			v-if="orderLocation && orderLocation.tripAdvisorUrl"
			class="order-feedback__block insicht insicht--fade-up"
		>
			<h2 class="order-feedback__block-title">
				<SVGLocation class="order-feedback__block-title-icon"/>
				<span v-html="$t('order.feedback.feedbackTitle')"/>
			</h2>
			<div
				class="order-feedback__block-text"
				v-html="$t('order.feedback.feedbackText', { channel: channelName })"
			/>
			<Button
				level="primary"
				:text="$t('order.feedback.feedbackButton')"
				:href="orderLocation.tripAdvisorUrl"
				target="_blank"
				arrow
				small
			/>
		</div>

		<Modal
			overlay-type="product-review"
			ref="reviewModal"
			:html-classes="['hide--header', 'show--modal']"
			narrow
			soft-close
			@softclose="onReviewSoftClose"
		>
			<template v-if="modalProduct">
				<div class="overlay-dialog">
					<h2
						class="overlay-dialog__title"
						v-html="$t('order.feedback.feedbackFormTitle')"
					/>
					<div
						class="overlay-dialog__text"
						v-html="modalProduct.name"
					/>
					<ProductReviewForm
						ref="reviewForm"
						:code="modalProduct.code"
						@reviewed="onProductReviewed"
					/>
				</div>
			</template>
		</Modal>

	</div>
</template>

<script>
import { mapGetters } from "vuex"
import {getUrlParams} from '@/utils/urlParams';
import CartOrderMixin from '@/mixins/CartOrderVueMixin';
import AccountReferral from '@/components/AccountReferral'
import FormInputRange from "@/components/FormInputRange"
import Button from "@/components/Button"
import Modal from "@/components/Modal"
import ProductReviewForm from "@/components/ProductReviewForm"
import ProductRating from '@/components/ProductRating';
import GeneralMessage from '@/components/GeneralMessage';
import TipInput from '@/components/TipInput'

export default {
	mixins: [
		CartOrderMixin,
	],
	components: {
		AccountReferral,
		FormInputRange,
		Button,
		Modal,
		ProductReviewForm,
		ProductRating,
		GeneralMessage,
		TipInput,
	},
	data() {
		return {
			tipSubmitted: false,
			tipResponse: {
				message: '',
				type: 'info',
			},
			modalProduct: null,
			productsReviewed: {}
		}
	},
	computed: {
		...mapGetters('loader', [
			'isLoading',
		]),
		...mapGetters('order', [
			'itemsWithoutAdditional',
			'additionalItems',
			'channelName',
			'cartDeliveryTime',
			'cartOrderNow',
			'shippingMethod',
			'payments',
			'total',
			'totalWithoutSurcharges',
			'billingAddress',
			'shippingAddress',
			'dropoffType',
			'notes',
			'useDishes',
			'discounts',
			'cartCo2Compensation',
			'cartCo2CompensationAmount',
			"cartTip",
			"cartTipEnabled",
			"cartTipDefault",
      "cartTipTypes",
      "cartTipType",
      "cartTipPercent",
      "cartTipVariant",
      "cartTipOption",
			'orderStates',
			'deliveryPrice',
			'tokenValue',
			'orderLocation',
			'orderSurveyUrl',
			'orderNpsSurveyUrl',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
		deliveryDate() {
			return this.cartDeliveryTime?.format('L')
		},
		deliveryTime() {
			return this.cartOrderNow
				? this.$t('checkout.success.orderNow')
				: this.cartDeliveryTime?.format('LT')
		},
		itemsEnhanced() {
			return this.itemsWithoutAdditional
				.reduce((items, item) => {
					if (!item.isTip && !item.isAdditional) {
						if (item.isEditable || item.isConfigurable) {

							item.allOptions = item.children
								.map(child => child.name)
								.join(', ')
						}

						items.push(item)
					}
					return items
				}, [])
		},
	},
	watch: {
	},
	methods: {
		formatAddress(address) {
			if (address) {
				let formattedAddress = []

				if (address.firstName && address.lastName) {
					formattedAddress.push(`${address.firstName} ${address.lastName}`);
				}

				if (address.street || address.streetNumber) {
					formattedAddress.push(`${address.street || ''} ${address.streetNumber || ''}`);
				}

				if (address.postcode || address.city) {
					formattedAddress.push(`${address.postcode || ''} ${address.city || ''}`);
				}
				if (address.company) {
					formattedAddress.unshift(address.company);
				}

				return formattedAddress.join(', ');
			}
		},
		onTipOption(index) {
			const option = this.cartTipVariant?.options?.[index]
			this.$store.dispatch('order/setTipOption', { value: index })
			this.$store.dispatch('order/setTipType', { value: option?.type })
		},
		onTipPercent(value) {
			this.$store.dispatch('order/setTipPercent', { value })
		},
		onTipValue(value) {
			this.$store.dispatch('order/adjustTip', { value })
		},
		async onTipSubmit() {
			this.tipSubmitted = true;
			await this.$store.dispatch('order/createTipOrder', { tip: this.cartTip });
		},
		onProductFeedback(item) {
			this.modalProduct = item;
			this.$refs.reviewModal.open();
		},
		onProductReviewed() {
			this.$refs.reviewModal.close();
			this.productsReviewed[this.modalProduct.code] = true;
			this.modalProduct = null;
		},
		onModalReviewClose() {
		},
		async onReviewSoftClose() {
			if (this.$refs.reviewForm.edited) {
				const confirmed = await this.$store.dispatch('dialog/showDialog', {
					title: this.$t('order.feedback.dialogTitle'),
					message: this.$t('order.feedback.dialogMessage'),
					confirm: this.$t('order.feedback.dialogConfirm'),
					cancel: this.$t('order.feedback.dialogCancel'),
				});
				if (confirmed) {
					return;
				}
			}
			this.$refs.reviewModal.close();
			this.modalProduct = null;
		},
	},
	async created() {
		this.loading = true;
		let {tokenValue: token, cancel, error} = getUrlParams();

		this.$store.dispatch('order/setIsFeedback', true)
		await this.$store.dispatch('initializeCheckoutSuccess', { token })
		await this.$store.dispatch('order/loadOrderLocation');

		if (cancel) {
			this.tipResponse.message = this.$t('order.feedback.tipCancel');
			this.tipResponse.type = 'warning';
		}
		else if (error) {
			this.tipResponse.message = this.$t('order.feedback.tipError')
			this.tipResponse.type = 'error'
		}

		this.loading = false;
	}
}
</script>

<style>

</style>
